import { useState } from "react";
import { ArrowLeft, ArrowRight, CheckCircle } from "lucide-react";
import { Reorder } from "framer-motion";

import TagInput from "../components/TagInput";
import { useNavigate } from "react-router-dom";
import Stepper from "../components/Stepper";
import heroTeacherLight from "../assets/images/hero-teacher-light-pexels.jpeg";
import heroTeacherDark from "../assets/images/hero-teacher-dark-pexels.jpeg";

interface CourseDraft {
  code: string;
  title: string;
  term: string;
  description: string;
  weeks: number;
  sessionsPerWeek: number;
  roster: string[]; // list of student emails

  // Learning design
  objectives: string[]; // ordered list – importance = order
  deliveryMode: "in-person" | "online" | "hybrid";

  // Optional cover image stored as data URL while we are client-side only
  coverImage: string | null;
}

export default function NewCourseOnboardingPage() {
  /* --------------------------------------------
   * State
   * ------------------------------------------*/
  const [step, setStep] = useState(0);
  const [data, setData] = useState<CourseDraft>({
    code: "",
    title: "",
    term: "",
    description: "",
    weeks: 12,
    sessionsPerWeek: 2,
    roster: [],
    objectives: [],
    deliveryMode: "in-person",
    coverImage: null,
  });

  // Tmp state for the new outcome text field (step 1)
  const [newOutcome, setNewOutcome] = useState("");

  const navigate = useNavigate();

  const steps = [
    "Basics",
    "Learning Design",
    "Structure",
    "Participants",
    "Review",
  ] as const;

  /* --------------------------------------------
   * Handlers
   * ------------------------------------------*/
  const next = () => setStep((s) => Math.min(s + 1, steps.length - 1));
  const prev = () => setStep((s) => Math.max(s - 1, 0));

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value } = e.target;

    const numericFields = ["weeks", "sessionsPerWeek"];

    setData((d) => ({
      ...d,
      [name]: numericFields.includes(name) ? Number(value) : value,
    }));
  };

  const handleSubmit = () => {
    // In a real app we would send the payload to Supabase here
    console.log("Course created:", data);
    navigate("/app/courses");
  };

  /* --------------------------------------------
   * Per-step content
   * ------------------------------------------*/
  let content: JSX.Element | null = null;

  if (step === 0) {
    content = (
      <div className="space-y-6">
        {/* Grid for short fields */}
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              Course code
            </label>
            <input
              type="text"
              name="code"
              value={data.code}
              onChange={handleChange}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
              placeholder="e.g. CS101"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              Term / Semester
            </label>
            <input
              type="text"
              name="term"
              value={data.term}
              onChange={handleChange}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
              placeholder="Spring 2026"
            />
          </div>
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">Course title</label>
          <input
            type="text"
            name="title"
            value={data.title}
            onChange={handleChange}
            className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
            placeholder="Intro to Computer Science"
          />
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">Brief description</label>
          <textarea
            name="description"
            value={data.description}
            onChange={handleChange}
            rows={4}
            className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
            placeholder="Students will learn fundamental CS concepts including algorithms, data structures and software design principles."
          />
        </div>

        {/* Cover image */}
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">Course cover image</label>
          <div className="space-y-2">
            {data.coverImage && (
              <img
                src={data.coverImage}
                alt="Course cover preview"
                className="h-40 w-full rounded-md object-cover"
              />
            )}
            <input
              type="file"
              accept="image/*"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                const reader = new FileReader();
                reader.onload = () => {
                  if (typeof reader.result === "string") {
                    setData((d) => ({ ...d, coverImage: reader.result as string }));
                  }
                };
                reader.readAsDataURL(file);
              }}
            />
          </div>
        </div>
      </div>
    );
  } else if (step === 1) {
    /* Learning design */
    const addOutcome = () => {
      const trimmed = newOutcome.trim();
      if (!trimmed) return;
      setData((d) => ({ ...d, objectives: [...d.objectives, trimmed] }));
      setNewOutcome("");
    };

    const updateOutcome = (index: number, value: string) => {
      setData((d) => {
        const next = [...d.objectives];
        next[index] = value;
        return { ...d, objectives: next };
      });
    };

    const removeOutcome = (index: number) => {
      setData((d) => {
        const next = d.objectives.filter((_, i) => i !== index);
        return { ...d, objectives: next };
      });
    };

    const moveOutcome = (index: number, direction: -1 | 1) => {
      setData((d) => {
        const next = [...d.objectives];
        const newIndex = index + direction;
        if (newIndex < 0 || newIndex >= next.length) return d; // no move
        const [item] = next.splice(index, 1);
        next.splice(newIndex, 0, item);
        return { ...d, objectives: next };
      });
    };

    content = (
      <div className="space-y-6">
        {/* Outcomes list */}
        <div>
          <label className="mb-2 block text-sm font-medium text-gray-700">
            Intended learning outcomes
          </label>

          {/* Add new outcome */}
          <div className="mb-4 flex gap-2">
            <input
              type="text"
              value={newOutcome}
              onChange={(e) => setNewOutcome(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addOutcome();
                }
              }}
              className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
              placeholder="e.g. Explain the fundamentals of algorithm analysis"
            />
            <button
              type="button"
              onClick={addOutcome}
              className="rounded-md bg-primary px-3 py-2 text-white shadow hover:bg-primary/90"
            >
              Add
            </button>
          </div>

          {data.objectives.length === 0 ? (
            <p className="text-sm text-gray-500">No outcomes added yet.</p>
          ) : (
            <Reorder.Group
              axis="y"
              values={data.objectives}
              onReorder={(newOrder) =>
                setData((d) => ({ ...d, objectives: newOrder }))
              }
              className="space-y-2"
            >
              {data.objectives.map((objective, i) => (
                <Reorder.Item
                  key={`${objective}-${i}`}
                  value={objective}
                  className="flex items-start gap-2 rounded-md border border-gray-200 bg-gray-50 p-3"
                >
                  <span className="cursor-move select-none text-xs font-semibold text-gray-500">
                    {i + 1}
                  </span>
                  <input
                    value={objective}
                    onChange={(e) => updateOutcome(i, e.target.value)}
                    className="flex-1 rounded-md border border-transparent bg-transparent px-2 py-1 text-sm focus:border-primary focus:bg-white focus:outline-none"
                  />
                  <button
                    type="button"
                    onClick={() => removeOutcome(i)}
                    className="rounded-md p-1 text-red-600 hover:bg-red-50"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="h-4 w-4"
                    >
                      <polyline points="3 6 5 6 21 6" />
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                      <line x1="10" y1="11" x2="10" y2="17" />
                      <line x1="14" y1="11" x2="14" y2="17" />
                    </svg>
                  </button>
                </Reorder.Item>
              ))}
            </Reorder.Group>
          )}
        </div>

        {/* Delivery mode */}
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">Delivery mode</label>
          <select
            name="deliveryMode"
            value={data.deliveryMode}
            onChange={handleChange}
            className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
          >
            <option value="in-person">In-person</option>
            <option value="online">Online</option>
            <option value="hybrid">Hybrid</option>
          </select>
        </div>
      </div>
    );
  } else if (step === 2) {
    /* Structure */
    content = (
      <div className="space-y-6">
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              Number of teaching weeks
            </label>
            <input
              type="number"
              name="weeks"
              value={data.weeks}
              onChange={handleChange}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
              min={1}
              max={52}
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              Sessions per week
            </label>
            <input
              type="number"
              name="sessionsPerWeek"
              value={data.sessionsPerWeek}
              onChange={handleChange}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
              min={1}
              max={7}
            />
          </div>
        </div>
      </div>
    );
  } else if (step === 3) {
    /* Participants */
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    content = (
      <div className="space-y-4">
        <label className="block text-sm font-medium text-gray-700">
          Student emails
        </label>

        <TagInput
          value={data.roster}
          onChange={(tags) => setData((d) => ({ ...d, roster: tags }))}
          placeholder="Type an email and press Enter"
          validate={(tag) => emailRegex.test(tag)}
        />

        <p className="text-xs text-gray-500">
          We’ll invite students via email once the course is created. You can
          skip this step and add them later.
        </p>
      </div>
    );
  } else if (step === 4) {
    /* Review */
    content = (
      <div className="space-y-4 text-sm">
        <h3 className="text-lg font-medium text-gray-800">Review</h3>
        <ul className="space-y-2">
          <li>
            <strong>Code:</strong> {data.code || "—"}
          </li>
          <li>
            <strong>Title:</strong> {data.title || "—"}
          </li>
          <li>
            <strong>Term:</strong> {data.term || "—"}
          </li>
          <li>
            <strong>Weeks:</strong> {data.weeks}
          </li>
          <li>
            <strong>Sessions / wk:</strong> {data.sessionsPerWeek}
          </li>
          <li>
            <strong>Delivery mode:</strong> {data.deliveryMode}
          </li>
          <li className="flex items-center gap-2">
            <strong>Cover image:</strong>
            {data.coverImage ? (
              <img
                src={data.coverImage}
                alt="Cover preview"
                className="h-10 w-20 rounded object-cover"
              />
            ) : (
              "—"
            )}
          </li>
          <li>
            <strong>Objectives:</strong> {data.objectives.length} defined
            {data.objectives.length > 0 && (
              <ul className="mt-1 list-inside list-decimal space-y-1 text-gray-700">
                {data.objectives.map((o, idx) => (
                  <li key={idx} className="text-xs">
                    {o}
                  </li>
                ))}
              </ul>
            )}
          </li>
          <li>
            <strong>Students:</strong> {data.roster.length}
            {data.roster.length > 0 && (
              <ul className="mt-1 list-inside list-disc space-y-1 text-gray-700">
                {data.roster.map((email) => (
                  <li key={email} className="text-xs">
                    {email}
                  </li>
                ))}
              </ul>
            )}
          </li>
        </ul>
      </div>
    );
  }

  /* --------------------------------------------
   * UI shell
   * ------------------------------------------*/
  return (
    <div className="mx-auto max-w-3xl space-y-8">
      {/* Hero */}
      <section className="relative overflow-hidden rounded-2xl">
        {/* Theme images */}
        <img
          src={heroTeacherLight}
          alt="Teacher guiding students during class"
          className="theme-light-only absolute inset-0 h-40 w-full object-cover sm:h-52"
        />
        <img
          src={heroTeacherDark}
          alt="Students watching astronomy projection in dim room"
          className="theme-dark-only absolute inset-0 h-40 w-full object-cover sm:h-52"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-white/70 via-white/40 to-white" />
        {/* Credits – light */}
        <p className="absolute bottom-1 right-2 text-[10px] text-gray-500 dark:hidden">
          Photo by{' '}
          <a
            href="https://www.pexels.com/@pixabay"
            target="_blank"
            rel="noreferrer"
            className="underline"
          >
            Pixabay
          </a>{' '}
          on{' '}
          <a
            href="https://www.pexels.com"
            target="_blank"
            rel="noreferrer"
            className="underline"
          >
            Pexels
          </a>
        </p>

        {/* Credits – dark */}
        <p className="absolute bottom-1 right-2 hidden text-[10px] text-gray-400 dark:block">
          Photo by{' '}
          <a
            href="https://www.pexels.com/@tima-miroshnichenko"
            target="_blank"
            rel="noreferrer"
            className="underline"
          >
            Tima Miroshnichenko
          </a>{' '}
          on{' '}
          <a
            href="https://www.pexels.com"
            target="_blank"
            rel="noreferrer"
            className="underline"
          >
            Pexels
          </a>
        </p>
      </section>
      {/* Header */}
      <header className="text-center">
        <h1 className="mb-2 text-3xl font-semibold text-primary">Create a new course</h1>
        <p className="text-sm text-gray-600">
          Follow the steps to set up your course. You can edit everything later.
        </p>
      </header>

      {/* Stepper */}
      <Stepper steps={steps as unknown as string[]} currentStep={step} />

      {/* Step content */}
      <section className="rounded-xl bg-white p-6 shadow-sm">{content}</section>

      {/* Controls */}
      <div className="flex justify-between">
        <button
          onClick={step === 0 ? () => navigate(-1) : prev}
          className="inline-flex items-center gap-2 rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          <ArrowLeft size={16} /> {step === 0 ? "Cancel" : "Back"}
        </button>

        {step < steps.length - 1 ? (
          <button
            onClick={next}
            className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-white shadow hover:bg-primary/90"
          >
            Next <ArrowRight size={16} />
          </button>
        ) : (
          <button
            onClick={handleSubmit}
            className="inline-flex items-center gap-2 rounded-md bg-green-600 px-4 py-2 text-sm font-medium text-white shadow hover:bg-green-700"
          >
            <CheckCircle size={16} /> Create course
          </button>
        )}
      </div>
    </div>
  );
}
